/**
 * runHarnessAgent —— runAgent 的同签名替换（引擎：服务端 DeepSeek Harness）。
 *
 * 与旧前端循环的区别：
 * - agent 循环、工具执行、审批 seam 全部在服务端；浏览器只收到标准化事件（SSE）；
 * - 一个 UI 会话（convId，usageMeta.sessionUuid）对应一个持久的 DSH 会话，历史由引擎维护；
 * - 审批：approval-request 事件 → 复用 UI 的 context.onRequestApproval 弹窗 → 应答 allow/deny。
 */

import type { HarnessEvent } from '@xiaotianshu/shared/harness';
import type { AIMessage, AIToolCall, AgentContext, ApprovalAnswer, ApprovalRequest } from '../types';
import {
  answerApproval,
  answerQuestion,
  bindHarnessSession,
  closeSession,
  createHarnessSession,
  fetchBoundHarnessSession,
  fetchSessionInfo,
  openEventStream,
  sendSessionMessage,
  SessionLedgerWriter,
  updateHarnessSession,
} from './harnessClient';
import { buildHistoryPrime } from './primeText';
import { extractExecuteActions, resolveApprovalArgs, type ExecuteActionLike } from './approvalArgs';
import { writeChapterContentToEditor } from '../utils/editorContent';
import type { RunAgentParams, RunAgentResult } from './agent';

/** UI 会话（convId）→ DSH 会话 id；映射持久化在 localStorage，页面刷新后仍可复用服务端会话 */
const sessionsByConv = new Map<string, string>();
/** 正在进行的回合数（见 isHarnessTurnActive 的说明） */
let activeTurns = 0;
/** UI 会话（convId）→ 创建时/最近一次同步的推理力度（用于会话中途变更时同步） */
const sessionEffortByConv = new Map<string, string | undefined>();
let harnessBookId = '';

// ── 会话映射持久化（localStorage）─────────────────────────────────────
// 目的：页面刷新 / HMR 重载 / 应用重启后，同一对话继续复用服务端的 DSH 会话，
// 让模型保留**完整**引擎上下文（含 tool/call 参数、工具结果、审批记录），
// 而不是重建会话 + 注入被截断的 primeText 摘要（旧行为会导致“中间操作记录丢失”，
// 例如模型不记得“把第七章第一段改成 测试”这次写库操作）。
// 服务端会话为内存态：服务端重启后会话失效，此时会校验失败并自动回退为新建会话。
const SESSION_MAP_STORAGE_KEY = 'xiaotianshu:harness-session-map';

interface PersistedSessionEntry {
  sid: string;
  effort?: string;
}

function loadPersistedSessions(): void {
  if (sessionsByConv.size > 0) return;
  try {
    const raw = localStorage.getItem(SESSION_MAP_STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw) as Record<string, PersistedSessionEntry>;
    for (const [convId, entry] of Object.entries(parsed)) {
      if (entry && typeof entry.sid === 'string' && entry.sid) {
        sessionsByConv.set(convId, entry.sid);
        if (typeof entry.effort === 'string') sessionEffortByConv.set(convId, entry.effort);
      }
    }
  } catch { /* 读取失败按空处理 */ }
}

function persistSessions(): void {
  try {
    const obj: Record<string, PersistedSessionEntry> = {};
    for (const [convId, sid] of sessionsByConv) {
      obj[convId] = { sid, ...(sessionEffortByConv.get(convId) ? { effort: sessionEffortByConv.get(convId) } : {}) };
    }
    localStorage.setItem(SESSION_MAP_STORAGE_KEY, JSON.stringify(obj));
  } catch { /* 持久化失败不影响会话功能 */ }
}

loadPersistedSessions();

/** 用 UI 原有审批弹窗的数据结构构造请求（与旧 executor 的 batchItems 形状一致） */
/**
 * 为一个待审批的改动算出**字段级 旧→新**对照。
 *
 * 只读现有数据（`window.api`），失败一律降级为空数组 —— 审批卡不能因为
 * "取旧值失败"就弹不出来，那会让写操作直接卡死。
 *
 * `long: true` 标记长文本字段（章节正文/设定内容）：界面据此改用 diff 视图渲染，
 * 而不是把两段几万字的正文并排糊一屏。
 */
async function buildFieldDiff(
  action: ExecuteActionLike,
): Promise<NonNullable<NonNullable<ApprovalRequest['batchItems']>[number]['fieldDiff']>> {
  const data = (action.data ?? {}) as Record<string, unknown>;
  if (action.action !== 'update' || !action.targetId) return [];
  const rows: Array<{ field: string; label: string; old: string; next: string; long?: boolean }> = [];
  const push = (field: string, label: string, oldValue: unknown, long = false) => {
    if (!(field in data)) return;                       // 只列真正要改的字段
    const next = data[field];
    const toText = (v: unknown) => (v === null || v === undefined ? '（空）' : String(v));
    const oldText = toText(oldValue);
    const nextText = toText(next);
    if (oldText === nextText) return;                   // 值没变就不列，免得刷屏
    rows.push({ field, label, old: oldText, next: nextText, ...(long ? { long: true } : {}) });
  };

  try {
    switch (action.target) {
      case 'chapter_content': {
        const ch = await window.api.getChapterMetadata(action.targetId);
        // 正文单独取（metadata 不含正文），它是这个 target 的主角
        let content: string | undefined;
        try { content = await window.api.getChapterContent(action.targetId); } catch { /* ignore */ }
        push('content', '正文', content, true);
        if (ch) {
          push('title', '标题', ch.title);
          push('summary', '摘要', ch.summary);
        }
        break;
      }
      case 'chapter': {
        const ch = await window.api.getChapterMetadata(action.targetId);
        if (ch) {
          push('title', '标题', ch.title);
          push('summary', '摘要', ch.summary);
          push('status', '状态', ch.status);
          push('target_words', '目标字数', ch.target_words);
          push('volume_id', '所属分卷', ch.volume_id);
        }
        push('content', '正文', undefined, true);   // create 才带正文；update 时正文走 chapter_content
        break;
      }
      case 'outline': {
        const outline = await window.api.getOutline(harnessBookId);
        push('content', '大纲内容', outline, true);
        break;
      }
      case 'character': {
        const all = await window.api.getCharacters(harnessBookId);
        const e = all.find((c) => c.id === action.targetId);
        if (e) {
          push('name', '名称', e.name);
          push('age', '年龄', e.age);
          push('strength', '战力', e.strength);
          push('faction_id', '所属势力', e.faction_id);
          push('description', '描述', e.description, true);
        }
        break;
      }
      case 'faction': {
        const all = await window.api.getFactions(harnessBookId);
        const e = all.find((f) => f.id === action.targetId);
        if (e) {
          push('name', '名称', e.name);
          push('parent_faction_id', '父势力', e.parent_faction_id);
          push('description', '描述', e.description, true);
        }
        break;
      }
      case 'setting': {
        const all = await window.api.getSettings(harnessBookId);
        const e = all.find((s) => s.id === action.targetId);
        if (e) {
          push('title', '标题', e.title);
          push('content', '内容', e.content, true);
        }
        break;
      }
      case 'item': {
        const all = await window.api.getItems(harnessBookId);
        const e = all.find((it) => it.id === action.targetId);
        if (e) {
          push('name', '名称', e.name);
          push('type', '类型', e.type);
          push('description', '描述', e.description, true);
        }
        break;
      }
      case 'volume': {
        const volumes = await window.api.getVolumes(harnessBookId);
        const e = volumes.find((v) => v.id === action.targetId);
        if (e) {
          push('title', '标题', e.title);
          push('description', '简介', e.description, true);
        }
        break;
      }
      default:
        break;
    }
  } catch {
    // 取旧值失败（离线/删了/接口变了）→ 退化成"没有对照"，但审批照常
    return rows;
  }
  return rows;
}

async function buildApprovalRequest(ev: Extract<HarnessEvent, { type: 'approval-request' }>): Promise<ApprovalRequest> {
  /**
   * 参数解析**逐路兜底**（见 approvalArgs.ts）：
   * 先信服务端解析好的 `argumentsParsed`，拿不到 actions 就退回原始 `argumentsText`
   * 重新解析（它可能是"JSON 的字符串"、可能只给了裸数组、也可能还没写全）。
   *
   * 以前只认 `argumentsParsed.actions` 这一条路：任何形状偏差都会得到空操作列表，
   * 审批卡弹不出来，于是走到"自动拒绝" —— 用户屏幕上什么都没有，
   * 模型却被告知 `the user rejected tool "execute"`。返回空列表即代表**彻底没解析出来**。
   */
  const { actions, description, atomic } = resolveApprovalArgs(ev);

  const batchItems: NonNullable<ApprovalRequest['batchItems']> = [];
  for (const action of actions) {
    let details: Record<string, unknown> = { ...(action.data ?? {}) };
    if (action.action === 'delete' && action.targetId) {
      try {
        switch (action.target) {
          case 'character': {
            const all = await window.api.getCharacters(harnessBookId);
            const e = all.find((c) => c.id === action.targetId);
            if (e) details = { name: e.name, age: e.age, strength: e.strength, description: e.description };
            break;
          }
          case 'faction': {
            const all = await window.api.getFactions(harnessBookId);
            const e = all.find((f) => f.id === action.targetId);
            if (e) details = { name: e.name, description: e.description };
            break;
          }
          case 'setting': {
            const all = await window.api.getSettings(harnessBookId);
            const e = all.find((s) => s.id === action.targetId);
            if (e) details = { title: e.title, content: e.content };
            break;
          }
          case 'item': {
            const all = await window.api.getItems(harnessBookId);
            const e = all.find((it) => it.id === action.targetId);
            if (e) details = { name: e.name, type: e.type, description: e.description };
            break;
          }
          case 'volume': {
            const volumes = await window.api.getVolumes(harnessBookId);
            const e = volumes.find((v) => v.id === action.targetId);
            if (e) details = { title: e.title, description: e.description };
            break;
          }
        }
      } catch { /* 详情获取失败不阻塞审批 */ }
    }
    if (action.target === 'chapter_content' && action.targetId) {
      try {
        const chapter = await window.api.getChapterMetadata(action.targetId);
        if (chapter) details = { ...details, chapterTitle: chapter.title };
      } catch { /* ignore */ }
    }

    // ── 改动的**字段级 旧→新**对照（供审批卡展开成详情弹窗审阅）──
    // 用户原话："点开审批标签能看出改了什么、旧内容与新内容分别是什么"。
    // 数据全在浏览器侧取（window.api 都有现成读接口），不需要服务端多传东西。
    const fieldDiff = await buildFieldDiff(action);
    batchItems.push({
      type: action.action as 'create' | 'update' | 'delete',
      target: action.target as ApprovalRequest['target'],
      targetId: action.targetId,
      details,
      ...(fieldDiff.length > 0 ? { fieldDiff } : {}),
    });
  }

  return {
    // 让界面能把这张卡与后续的 approval-answered（含**超时**）对上号：
    // 服务端结算时前端要就地收尾这张卡，否则用户对着失效的卡操作、后面的审批也弹不出来。
    requestId: ev.requestId,
    type: batchItems[0]?.type ?? 'update',
    target: batchItems[0]?.target ?? 'character',
    description: description ?? `批量执行 ${actions.length} 个操作`,
    details: { total: actions.length, atomic },
    batchItems,
  };
}

function toolResultSummary(content: string, success: boolean): string {
  if (!success) return content.slice(0, 120);
  try {
    const parsed = JSON.parse(content) as Record<string, unknown> | unknown[] | null;
    if (Array.isArray(parsed)) return `返回 ${parsed.length} 条记录`;
    if (parsed && typeof parsed === 'object') {
      const p = parsed as Record<string, unknown>;
      if (typeof p.summary === 'string') return p.summary;
      if (typeof p.message === 'string') return p.message;
      if (Array.isArray(p.data)) return `返回 ${p.data.length} 条数据`;
      if (Array.isArray(p.results)) {
        const s = p.summary as Record<string, unknown> | undefined;
        return `成功 ${s?.succeeded ?? p.results.filter((r) => (r as Record<string, unknown>).success).length} 个操作`;
      }
      if (typeof p.error === 'string') return p.error;
    }
  } catch { /* 非 JSON 走原文 */ }
  // 兜底：绝不把裸 JSON 上屏
  return '操作完成';
}

/**
 * 运行一次 agent 回合。params.messages 的**最后一条**是用户新消息；
 * 历史保留在持久的 DSH 会话中（首次调用时自动创建）。
 */
/**
 * 外部入口：包一层"进行中回合"计数。
 *
 * 计数必须在**所有**退出路径上递减（正常结束、失败、用户中止、
 * 以及用户答复审批后回合继续跑完），所以用 try/finally 包住整个实现，
 * 而不是散落在 finish()/fail()/cleanup() 里 —— 那种写法迟早会漏一条路径。
 */
export async function runHarnessAgent(params: RunAgentParams): Promise<RunAgentResult> {
  activeTurns += 1;
  try {
    return await runHarnessAgentInner(params);
  } finally {
    activeTurns = Math.max(0, activeTurns - 1);
  }
}

async function runHarnessAgentInner(params: RunAgentParams): Promise<RunAgentResult> {
  const { model, provider, reasoningEffort, context, signal, usageMeta, callbacks = {} } = params;
  /**
   * 附着模式：**不发新消息**，只是接到一个已经存在的服务端会话上，
   * 把它还挂着的审批/提问重新接回来（服务端在订阅时会补发），继续等用户答复。
   *
   * 用途：页面刷新/重连后，服务端那个回合还停在工具调用上等着（审批不设超时），
   * 但旧事件早已发过（且 `replay=0` 不回放），新页面就再也看不见那张卡、也永远答不了。
   * 附着一次即可闭环：卡片回来 → 用户答复 → 回合继续 → 结果照常渲染。
   */
  const attachOnly = params.attachOnly === true;

  const convId = usageMeta?.sessionUuid ?? `conv-${Date.now()}`;
  const lastMessage = params.messages[params.messages.length - 1];
  const userText = lastMessage?.role === 'user' ? lastMessage.content : '';
  if (!userText && !attachOnly) throw new Error('缺少用户消息');

  harnessBookId = context.bookId;

  // 创建或复用 DSH 会话。
  //
  // 解析顺序（**跨设备优先**）：
  //   1. 本设备 localStorage 的映射（最快，且刷新后仍有效）；
  //   2. 数据库里绑定的 harness_session_id —— 这是"换设备仍能接回原会话"的关键：
  //      localStorage 只对单个浏览器有效，换设备后必然为空，此前就是在这里退化成
  //      "新建会话 + 注入被截断的摘要"，造成"AI 不记得下午聊过什么"。
  //   找到 sid 后校验存活；失效则从事件流水无损重建（见 primeText 的构造）。
  let harnessSessionId = sessionsByConv.get(convId);
  if (!harnessSessionId) {
    const bound = await fetchBoundHarnessSession(convId);
    if (bound) {
      harnessSessionId = bound;
      sessionsByConv.set(convId, bound);
      persistSessions();
    }
  }
  /**
   * 内存里的会话没了（服务重启/空闲回收），但 `harness_session_id` 还在：
   * 不要丢掉它去新建 —— 0.2.x 的 JSONL 日志就在磁盘上，带回去让服务端
   * `agents.resume` 恢复完整 transcript，这才是"隔天/换设备不失忆"。
   * 只有服务端确认日志也不存在时，它才会用同一个 id 新建。
   */
  let resumeSessionId: string | undefined;
  if (attachOnly && !harnessSessionId) {
    // 附着的前提就是"服务端已经有一个会话"；没有就别凭空造一个（那会变成空回合）
    throw new Error('没有可附着的会话');
  }
  if (harnessSessionId) {
    let needsResume = false;
    try {
      // 校验会话仍有效且与本轮请求的模型/供应商一致（不同则不可复用）
      const info = await fetchSessionInfo(harnessSessionId);
      // 附着模式**不校验模型/供应商**：目的是答复那张早就发出去的审批卡，
      // 而会话是模型绑定的 —— 用户刷新后可能已切了模型，这里若按"不一致"处理
      // 就会把还挂着回合的会话映射删掉，反而把用户的待办丢掉。
      if (!attachOnly && (info.model !== model || info.provider !== provider)) {
        // 模型变了但日志还在：先关掉内存里的旧会话（释放 JSONL 写句柄），
        // 再用同一个 id resume，既换模型又不丢历史。直接 resume 会撞
        // SessionAlreadyOwnedError，直接新建则会丢上下文。
        await closeSession(harnessSessionId).catch((): void => undefined);
        needsResume = true;
      }
    } catch {
      // 404 = 服务重启/空闲回收后内存里没了；JSONL 日志还在，带回去 resume。
      needsResume = true;
    }

    if (needsResume) {
      sessionsByConv.delete(convId);
      sessionEffortByConv.delete(convId);
      persistSessions();
      // 旧 0.1.x 的 Postgres 日志没有 JSONL 文件，服务端 stat 不到时会用
      // 同一个 id 新建（不会报 AlreadyExists），并注入 primeText 摘要。
      resumeSessionId = harnessSessionId;
      harnessSessionId = undefined;
      // 附着模式下会话已经不在（服务端重启/被回收）→ 无从附着，如实失败即可，
      // **绝不能**顺手新建一个：那会变成一个空回合，而用户其实什么都没发。
      if (attachOnly) throw new Error('原会话已不存在，无法附着');
    }
  }
  if (!harnessSessionId) {
    // 附着模式在上一段已经保证"必须有会话"，走到这里说明没有 —— 直接失败（见上面的 throw）
    const history = params.messages.slice(0, -1);
    // 摘要：包含工具调用参数与最近记录（见 buildHistoryPrime），
    // 保证重建上下文时模型仍能看到“测试 修改/删除”这类中间操作。
    // 即使带着 resumeSessionId 也照传：服务端只在**新建**时注入，
    // resume 到 JSONL 时日志本身已完整，会忽略它。
    const primeText = buildHistoryPrime(history);
    const created = await createHarnessSession({
      bookId: context.bookId,
      chapterId: context.chapterId,
      provider: (provider as 'deepseek' | 'openrouter') ?? 'deepseek',
      model,
      reasoningEffort,
      sessionUuid: convId,
      sessionId: resumeSessionId,
      primeText: primeText || undefined,
    });
    harnessSessionId = created.sessionId;
    sessionsByConv.set(convId, harnessSessionId);
    sessionEffortByConv.set(convId, reasoningEffort);
    persistSessions();
    // 同步绑定到数据库：换设备/清缓存后仍能查到"这个对话挂在哪个引擎会话上"
    void bindHarnessSession(convId, harnessSessionId);
  } else if (sessionEffortByConv.get(convId) !== reasoningEffort) {
    // 复用会话但推理力度变化：同步到服务端（立即生效于下一轮请求）
    await updateHarnessSession(harnessSessionId, { reasoningEffort }).catch((): void => undefined);
    sessionEffortByConv.set(convId, reasoningEffort);
    persistSessions();
  }
  const sid = harnessSessionId;

  return new Promise<RunAgentResult>((resolve, reject) => {
    let content = '';            // 最终回答（回合结束时的保存内容）
    let thinking = '';           // 全回合思考（结果字段）
    let stepText = '';           // 当前步骤的文本缓冲（工具步骤=说明文字；最终步骤=答案）
    let stepThinking = '';       // 当前步骤的思考缓冲
    let stepHasToolCall = false;
    let finalText = '';          // 最后一步（非工具）的完整文本 → 回合结束时统一回放
    let iterations = 0;
    let done = false;
    const toolMessages: AIMessage[] = [];
    const toolNameByCall = new Map<string, string>();
    const callArgs = new Map<string, string>();   // callId → argumentsText（execute 刷新用）
    let currentAssistant: AIMessage | null = null;

    // ── 会话事件流水：完整落库（机制见 harnessClient 的 SessionLedgerWriter）──
    // 目的：把"引擎收到的每一个事件"变成数据库里的权威历史，使换设备/服务重启后
    // 都能无损重建 —— 而不是像以前那样只剩一份被截断的文本摘要。
    const ledger = new SessionLedgerWriter(convId, { modelName: model, provider });
    /** 本次 run 内的回合序号（从 1 开始；单次 run 只服务一个回合） */
    let ledgerTurn = 0;
    /** 当前步骤的 reasoning 累积：delta 是逐字推来的，必须合并成一条再落库 */
    let ledgerReasoning = '';
    /** 已落库的 user-message 文本，避免与引擎回显重复记录 */
    const ledgerUserTexts = new Set<string>();

    /** 记录一条用户消息（去重：引擎会把同一条回显成 user-message） */
    const ledgerUserMessage = (text: string): void => {
      if (!text || ledgerUserTexts.has(text)) return;
      ledgerUserTexts.add(text);
      ledger.record('user-message', { text, source: 'client' }, ledgerTurn);
    };

    /** 结转当前步骤的 reasoning（步骤边界/回合结束时调用一次） */
    const flushReasoning = (): void => {
      if (!ledgerReasoning) return;
      ledger.record('reasoning', { text: ledgerReasoning, step: iterations }, ledgerTurn);
      ledgerReasoning = '';
    };
    /**
     * 待审批队列（替代原先的单槽位 `approvalPending`）。
     *
     * 一个回合里可能连续来多个审批：`execute` 是独占调度，前一个被结算
     * （用户点了 / 回合被取消 / 会话结束）之后才轮到下一个。原先单槽位 + `if (pending) break;`
     * 会把第二个起的审批**直接丢掉**（不弹卡、无人应答）→ （当时服务端还有 120 秒超时，会按"拒绝"结算）
     * → 模型被告知"用户拒绝"。生产上"多条 execute 都说用户拒绝、用户没操作过"就是这个。
     */
    const approvalQueue: Array<Extract<HarnessEvent, { type: 'approval-request' }>> = [];
    /** 服务端已结算的 requestId：不再为它们弹已经失效的卡 */
    const settledApprovalIds = new Set<string>();
    /**
     * 服务端已结算的 questionId（用户答了 / 未收到回答）。
     *
     * 与 `settledApprovalIds` 同一个道理，但后果不同：提问没等到回答时界面按约 resolve 一个空回答，
     * 而服务端那边**早已把这条提问删掉**，此时再 POST 只会得到 400
     * 「提问已过期或不存在」，界面上凭空多一条"⚠ 提问处理失败"。有这个集合就能跳过。
     */
    const settledQuestionIds = new Set<string>();
    let approvalBusy = false;

    /** 依次处理队列里的审批；被服务端结算过的直接跳过。 */
    const pumpApprovals = async (): Promise<void> => {
      if (approvalBusy) return;
      approvalBusy = true;
      try {
        for (;;) {
          const ev = approvalQueue.shift();
          if (!ev || done) break;
          if (settledApprovalIds.has(ev.requestId)) {
            settledApprovalIds.delete(ev.requestId);
            continue;
          }
          try {
            const request = await buildApprovalRequest(ev);
            const actions = request.batchItems ?? [];
            if (actions.length === 0) {
              // 防御：参数解析失败不得展示"人物 total:0"这类虚假审批卡。
              // 但**必须把"这不是用户拒绝"告诉服务端**（systemDenyReason）：否则模型只会看到
              // `the user rejected tool "execute"`，把系统解析失败当成用户不同意，
              // 于是改创作方向或反复重试 —— 而用户屏幕上根本没有过这张卡。
              callbacks.onAgentMessage?.('⚠ 审批参数解析失败，操作已自动拒绝。');
              // 完整落库：自动拒绝也留档（否则重建时看不出"这次操作是被系统拒的"）
              ledger.record('approval-answered', {
                requestId: ev.requestId,
                decision: 'deny',
                systemDenyReason: 'parse-failed',
                automatic: true,
              }, ledgerTurn);
              await answerApproval(sid, ev.requestId, 'deny', { systemDenyReason: 'parse-failed' })
                .catch((): void => undefined);
              continue;
            }
            // 若这张卡在用户点之前就被服务端结算了，onApprovalSettled 会把它就地收尾
            // 并 resolve(false)；此时再 answer 只会得到"已过期"，所以结算过的跳过 answer。
            // 等用户决策期间看门狗不计时（审批不设超时）。
            beginAwaitUser();
            let answer: ApprovalAnswer;
            let systemDenyReason: ApprovalAnswer['systemDenyReason'];
            try {
              if (context.onRequestApproval) {
                answer = await context.onRequestApproval(request);
              } else {
                // 没有审批界面 → 只能自动拒绝。这同样**不是用户点的拒绝**，如实标注，
                // 免得模型以为"用户不同意"（这条路径上用户连卡都看不到）。
                answer = { approved: false };
                systemDenyReason = 'no-handler';
                callbacks.onAgentMessage?.('⚠ 当前界面没有可用的审批通道，操作已自动拒绝。');
              }
            } finally {
              endAwaitUser();
            }
            if (settledApprovalIds.has(ev.requestId)) {
              settledApprovalIds.delete(ev.requestId);
              continue;
            }
            // 用户的意见随应答一起送到服务端 → 由服务端注入会话，AI 下一步就能读到。
            // systemDenyReason 只在**拒绝**时带（同意的应答带上会让服务端误注入"系统拒绝"说明）
            const denyReason = answer.approved ? undefined : (answer.systemDenyReason ?? systemDenyReason);
            // 完整落库：**用户的决策与意见**。
            // 这些内容以前完全不入库 —— 而"用户为什么拒绝、同意时补了什么要求"
            // 恰恰是事后最需要复盘的上下文（AI 的行为很大程度上由它决定）。
            ledger.record('approval-answered', {
              requestId: ev.requestId,
              decision: answer.approved ? 'allow' : 'deny',
              comment: answer.comment,
              itemComments: answer.itemComments,
              systemDenyReason: denyReason,
              automatic: false,
            }, ledgerTurn);
            await answerApproval(sid, ev.requestId, answer.approved ? 'allow' : 'deny', {
              ...(answer.comment ? { comment: answer.comment } : {}),
              ...(answer.itemComments?.length ? { itemComments: answer.itemComments } : {}),
              ...(denyReason ? { systemDenyReason: denyReason } : {}),
            });
          } catch (e) {
            /**
             * 兜底拒绝：走到这里说明**界面/网络这一侧出错了**（例如取旧的字段对照时抛错、
             * 面板回调 reject、应答 POST 失败），用户很可能根本没来得及做决定。
             * 所以：
             * 1) 必须让用户看见（不静默）；
             * 2) 必须告诉服务端这**不是用户点的拒绝**（systemDenyReason），
             *    否则模型只会读到 `the user rejected tool "execute"`，把它当成"用户不同意"。
             */
            callbacks.onAgentMessage?.(`⚠ 审批处理失败，操作已自动拒绝：${e instanceof Error ? e.message : String(e)}`);
            ledger.record('approval-answered', {
              requestId: ev.requestId,
              decision: 'deny',
              systemDenyReason: 'handler-error',
              automatic: true,
            }, ledgerTurn);
            await answerApproval(sid, ev.requestId, 'deny', { systemDenyReason: 'handler-error' })
              .catch((): void => undefined);
          }
        }
      } finally {
        approvalBusy = false;
      }
    };
    let streamClosed = false;
    const pendingReplays: Array<Promise<void>> = [];

    // 打字机式回放：回合结束时把最终回答按增量喂给面板（旧引擎 callStream 的视觉等价）。
    // 只在回合结束统一回放一次，避免"连续多个非工具步骤"导致内容/思考交错闪现。
    const sleep = (ms: number): Promise<void> => new Promise((r) => setTimeout(r, ms));
    const typeOut = async (text: string, emit: (partial: string) => void): Promise<void> => {
      for (let i = 0; i < text.length; i += 3) {
        if (done) return;
        emit(text.slice(0, i + 3));
        await sleep(14);
      }
      // 确保最终完整文本落位
      if (!done) emit(text);
    };

    /** execute 成功写库后：触发旧面板的章节结构/资料库刷新；当前章节被改写时回写编辑器 */
    const refreshAfterExecute = (callId: string, ctx: AgentContext): void => {
      const argsText = callArgs.get(callId) ?? '';
      /**
       * 与审批卡同一套解析兜底（见 approvalArgs.ts）：tool/call 事件里的参数
       * 既可能是对象、也可能是"JSON 的字符串"，还可能只给了裸数组。
       * 以前这里只认 `parsed.actions`：形状一变就得到空列表 → 界面**不刷新**
       * （章节列表/资料库/编辑器都停在旧内容），用户以为写库失败。
       */
      const actions: ExecuteActionLike[] = extractExecuteActions(argsText);

      let touchedStructure = false;
      let touchedWorld = false;
      let editingChapterContent: string | null = null;
      for (const act of actions) {
        if (act.target === 'volume' || act.target === 'chapter' || act.target === 'chapter_content') {
          touchedStructure = true;
          if (act.target === 'chapter_content' && act.targetId === ctx.chapterId
            && act.data && typeof act.data.content === 'string') {
            editingChapterContent = act.data.content;
          }
        }
        if (act.target === 'character' || act.target === 'faction' || act.target === 'setting'
          || act.target === 'item' || act.target === 'outline') {
          touchedWorld = true;
        }
      }
      if (touchedWorld) ctx.onWorldRefresh?.();
      if (touchedStructure) ctx.onChapterListRefresh?.();
      if (editingChapterContent !== null && ctx.editor) {
        // 当前打开章节被 AI 改写：回写编辑器（内容→HTML 的转换是**幂等**的）并持久化格式。
        // 以前这里无条件按 \n 分段、每段再包一层 <p>：AI 发来的正文本身已经是
        // `<p>…</p><p>…</p>`，于是被包成 `<p><p>…</p></p>`，浏览器解析后
        // 正文开头/末尾各多一个空段落，随后 onAutoSave 还会把它存进数据库
        // （详见 utils/editorContent.ts 的说明）。
        // emitUpdate:false —— 内容已由服务端写库，程序化回写不应触发 onUpdate，
        // 否则会被误判为“未保存”并触发自动保存/草稿快照（导致离开再返回时提示未保存草稿）。
        writeChapterContentToEditor(ctx.editor, editingChapterContent);
        ctx.onAutoSave?.();
      }
    };

    // ── 轨迹追踪（问题排查）：事件 + 回调发出序列，回合结束回传服务端 ──
    const trace: Array<{ t: number; k: string; s: string }> = [];
    const tr = (k: string, s: string): void => {
      trace.push({ t: Date.now(), k, s: s.length > 120 ? s.slice(0, 120) + '…' : s });
      if (trace.length > 3000) trace.splice(0, trace.length - 3000);
    };
    const flushTrace = (): void => {
      try {
        // 浏览器控制台可查（DevTools 控制台直接可见；搜 [harness]）
        // eslint-disable-next-line no-console
        console.log('[harness] trace', { sessionId: sid, convId, trace });
      } catch { /* ignore */ }
      try {
        void fetch('/api/harness/debug-trace', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ sessionId: sid, convId, trace: trace.slice(-1200) }),
        }).catch((): void => undefined);
      } catch { /* ignore */ }
    };

    // ── 旧面板兼容层：工具上报队列 ─────────────────────────────
    // 旧 ActivityFeed 的 pendingToolItemIdRef 是单槽设计（旧循环串行工具），
    // 而 Harness 引擎可并行执行工具（多个 tool/call），因此：
    // 按次序一次只向 UI 上报一个"调用中"标签；结果回来后补齐下一个。
    const pendingToolCalls: Array<{ callId: string; name: string; argsText: string }> = [];
    const pendingToolResults = new Map<string, { success: boolean; content: string }>();
    let activeToolCallId: string | null = null;

    const pumpToolQueue = (): void => {
      if (activeToolCallId !== null || pendingToolCalls.length === 0) return;
      const next = pendingToolCalls.shift()!;
      activeToolCallId = next.callId;
      tr('EMIT', `onToolCall ${next.name} args=${next.argsText.slice(0, 50)}`);
      callbacks.onToolCall?.(next.name);
      callbacks.onToolCallDetail?.(next.name, next.argsText);
    };

    const reportToolResult = (callId: string, success: boolean, content: string): void => {
      if (activeToolCallId !== callId) {
        // 结果乱序（并行工具）：先缓存，待轮到该调用时补报
        pendingToolResults.set(callId, { success, content });
        return;
      }
      const name = toolNameByCall.get(callId) ?? '';
      callbacks.onToolResultDetail?.(name, success, content);
      tr('EMIT', `onToolResult ${name} ${success ? 'OK' : 'FAIL'} len=${content.length}`);
      callbacks.onToolResult?.(name, success, toolResultSummary(content, success));
      activeToolCallId = null;
      pumpToolQueue();
      // 补报已缓存结果
      while (activeToolCallId !== null && pendingToolResults.has(activeToolCallId)) {
        const cached = pendingToolResults.get(activeToolCallId)!;
        pendingToolResults.delete(activeToolCallId);
        const cachedName = toolNameByCall.get(activeToolCallId) ?? '';
        callbacks.onToolResultDetail?.(cachedName, cached.success, cached.content);
        callbacks.onToolResult?.(cachedName, cached.success, toolResultSummary(cached.content, cached.success));
        activeToolCallId = null;
        pumpToolQueue();
      }
    };

    const close = openEventStream(sid, onEvent, (state) => {
      // 断线由 EventSource 自动重连；这里只把"已连上"告诉调用方
      //（附着模式没有 turn-end 可等，重试/报错全靠这个信号）
      if (state === 'open') callbacks.onStreamOpen?.();
    }, { replay: false });

    const cleanup = (): void => {
      if (streamClosed) return;
      streamClosed = true;
      disarmWatchdog();
      signal.removeEventListener('abort', onAbort);
      close();
    };

    const finish = (): void => {
      if (done) return;
      done = true;
      flushTrace();
      // 工具标签兜底：未闭环的调用补报失败，避免"永不终止"的标签
      if (activeToolCallId !== null) {
        const name = toolNameByCall.get(activeToolCallId) ?? '';
        callbacks.onToolResultDetail?.(name, false, '工具结果未返回（回合已结束）');
        callbacks.onToolResult?.(name, false, '工具结果未返回（回合已结束）');
        activeToolCallId = null;
      }
            for (const pending of pendingToolCalls) {
              const name = pending.name;
              tr('EMIT', `onToolResult(flush) ${name} FAIL`);
              callbacks.onToolResultDetail?.(name, false, '工具结果未返回（回合已结束）');
              callbacks.onToolResult?.(name, false, '工具结果未返回（回合已结束）');
            }
      pendingToolCalls.length = 0;
      cleanup();
      // 落库收尾：确保本轮全部事件都写进流水**之后**才 resolve。
      // 为什么必须等：调用方在 resolve 后会立刻把会话落库/切界面，
      // 若流水还在飞，用户可能看到"回答完了但历史里没有这一轮"。
      // 落库失败不阻塞结束（drain 内部已吞掉错误并记日志）。
      void ledger.drain().then(() => {
        resolve({ content, thinking: thinking.trim(), toolMessages });
      });
    };

    const fail = (err: unknown): void => {
      if (done) return;
      done = true;
      cleanup();
      reject(err instanceof Error ? err : new Error(String(err)));
    };

    const onAbort = (): void => {
      // 中止：关闭引擎会话（取消进行中的轮次），按旧约定抛出"请求已取消"
      sessionsByConv.delete(convId);
      persistSessions();
      void closeSession(sid).catch((): void => undefined);
      fail(new Error('请求已取消'));
    };
    if (signal.aborted) {
      onAbort();
      return;
    }
    signal.addEventListener('abort', onAbort, { once: true });

    /**
     * 回合看门狗：SSE 一旦丢失 `turn-end`（断线未恢复、服务端异常退出），
     * 这个 Promise 永不 settle —— `isGenerating` 会一直为 true、编辑器被锁死，
     * 用户只能手点停止。按"事件间隔"超时：只要还在持续收到事件就不断续期。
     *
     * ⚠️ **等用户决策的时间不计入**：审批与提问都不设超时（用户可能要反复看方案再决定），
     * 服务端会一直等着。如果这里照旧按 120 秒掐掉，"服务端还在等、界面却宣布超时"
     * 就自相矛盾了 —— 而且用户其实什么都没做错。所以只要还有待审批/待回答的卡片，
     * 就把看门狗**收起来**，等它们全部结算后再重新武装。
     */
    const turnTimeoutMs = Number(import.meta.env.VITE_HARNESS_TURN_TIMEOUT_MS) > 0
      ? Number(import.meta.env.VITE_HARNESS_TURN_TIMEOUT_MS)
      : 120_000;
    let watchdog: ReturnType<typeof setTimeout> | null = null;
    /** 正在等用户决策的交互数量（审批 + 提问） */
    let awaitingUser = 0;
    const armWatchdog = (): void => {
      if (done) return;
      if (watchdog) clearTimeout(watchdog);
      if (awaitingUser > 0) {
        // 用户在思考：不算"无响应"。把它收起来，别一边等服务端一边喊超时。
        watchdog = null;
        return;
      }
      watchdog = setTimeout(() => {
        fail(new Error(`AI 回合超时（${Math.round(turnTimeoutMs / 1000)} 秒无响应），已中止本次请求`));
      }, turnTimeoutMs);
    };
    function disarmWatchdog(): void {
      if (watchdog) clearTimeout(watchdog);
      watchdog = null;
    }
    /** 等用户决策的开始/结束；结束时若已无待答项则重新武装看门狗 */
    const beginAwaitUser = (): void => { awaitingUser += 1; armWatchdog(); };
    const endAwaitUser = (): void => { awaitingUser = Math.max(0, awaitingUser - 1); armWatchdog(); };
    armWatchdog();

    function onEvent(ev: HarnessEvent): void {
      if (done) return;
      // 只要还在收事件就续期，避免长回答被误判为超时
      armWatchdog();
      // 轨迹：记录原始事件（关键信息）
      if (ev.type === 'text-delta' || ev.type === 'reasoning-delta') {
        // 增量事件只记长度，避免轨迹暴涨
        if ((ev.text?.length ?? 0) > 0) tr('EVT', `${ev.type}+${ev.text.length}`);
      } else if (ev.type === 'tool-call') {
        tr('EVT', `tool-call ${ev.name} call=${ev.callId.slice(0, 10)} args=${String(JSON.stringify(ev.arguments)).slice(0, 60)}`);
      } else if (ev.type === 'tool-result') {
        tr('EVT', `tool-result call=${ev.callId.slice(0, 10)} ${ev.success ? 'OK' : 'FAIL'} len=${String(ev.content).length}`);
      } else if (ev.type === 'assistant-message') {
        tr('EVT', `assistant-message step_tool=${stepHasToolCall} text_len=${ev.text.length} text=${ev.text.slice(0, 60)}`);
      } else if (ev.type === 'turn-end' || ev.type === 'approval-request' || ev.type === 'error') {
        tr('EVT', `${ev.type} ${JSON.stringify(ev).slice(0, 80)}`);
      } else if (ev.type === 'user-message') {
        tr('EVT', `user-message len=${ev.text.length}`);
      } else {
        tr('EVT', ev.type);
      }

      switch (ev.type) {
        case 'text-delta': {
          // 统一缓冲，步骤结束时一次性决策（工具步骤=静态对话；最终步骤=打字机回放）：
          // 避免"半流式"导致同一句话发两次
          stepText += ev.text;
          break;
        }

        case 'reasoning-delta': {
          thinking += ev.text;
          stepThinking += ev.text;
          // 完整落库：逐字 delta 先在内存累积，步骤边界/回合结束时合并成一条写库
          // （一条一条写会把数据库打爆：一个回答可能几千个 delta）
          ledgerReasoning += ev.text;
          // 实时流入思考标签（模型何时思考，标签就在何时出现并滚动）
          callbacks.onThinking?.(stepThinking);
          break;
        }

        case 'tool-call': {
          if (!stepHasToolCall) {
            stepHasToolCall = true;
            // 注意：不在 tool-call 时 flush 已缓冲文本——
            // 该文本属于本步骤对话，统一在 assistant-message 时一次性呈现
          }
          // 完整落库：**工具调用及其参数**（`query` 到底查了什么，以前从未被保存）
          ledger.record('tool-call', {
            callId: ev.callId,
            name: ev.name,
            arguments: ev.arguments,
            argumentsParsed: ev.argumentsParsed,
          }, ledgerTurn);
          toolNameByCall.set(ev.callId, ev.name);
          const rawArgsText = typeof ev.arguments === 'string' ? ev.arguments : JSON.stringify(ev.arguments);
          callArgs.set(ev.callId, rawArgsText);
          pendingToolCalls.push({
            callId: ev.callId,
            name: ev.name,
            argsText: rawArgsText,
          });
          pumpToolQueue();
          const call: AIToolCall = {
            id: ev.callId,
            type: 'function',
            function: { name: ev.name, arguments: typeof ev.arguments === 'string' ? ev.arguments : JSON.stringify(ev.arguments) },
          };
          if (currentAssistant && currentAssistant.tool_calls && currentAssistant.tool_calls.length > 0) {
            currentAssistant.tool_calls.push(call);
          } else {
            currentAssistant = { role: 'assistant', content: '', tool_calls: [call] };
            toolMessages.push(currentAssistant);
          }
          break;
        }

        case 'tool-result': {
          if (currentAssistant && currentAssistant.tool_calls) {
            toolMessages.push({ role: 'tool', content: ev.content, tool_call_id: ev.callId });
          }
          // 完整落库：**工具结果原文**（可能含整章正文 —— 不做任何截断）。
          // 事件本身不带工具名（服务端规范化时丢了），所以从 callId 映射补回来，
          // 否则重建时无法知道这条结果属于哪个工具。
          ledger.record('tool-result', {
            callId: ev.callId,
            name: toolNameByCall.get(ev.callId) ?? ev.name ?? '',
            success: ev.success,
            content: ev.content,
          }, ledgerTurn);
          reportToolResult(ev.callId, ev.success, ev.content);
          // execute 成功后：同步旧面板的数据刷新（章节结构/资料库/编辑器回写）
          if (toolNameByCall.get(ev.callId) === 'execute' && ev.success) {
            refreshAfterExecute(ev.callId, context);
          }
          break;
        }

        case 'approval-request': {
          // 完整落库：审批请求（含待写入的具体参数）—— "AI 想改什么"的原始凭据
          ledger.record('approval-request', {
            requestId: ev.requestId,
            toolName: ev.toolName,
            callId: ev.callId,
            reason: ev.reason,
            argumentsText: ev.argumentsText,
            argumentsParsed: ev.argumentsParsed,
          }, ledgerTurn);
          // ⚠️ 不能只留一个槽位然后 `if (approvalPending) break;`：那样同一回合里
          // **第二个起的审批会被静默丢弃**（不弹卡、无人应答）→ （当时服务端还有 120 秒超时，会按"拒绝"结算）
          // → 模型被告知 `the user rejected tool "execute"`，而用户什么都没做。
          // 生产上就是这么出现"多条 execute 显示用户拒绝"的。
          // 现在排队：一个处理完（用户点过，或被服务端结算）再弹下一个。
          approvalQueue.push(ev);
          void pumpApprovals();
          break;
        }

        case 'approval-answered': {
          // 服务端已经结算了这个请求（用户点了、轮次取消、或**超时**）。
          // 必须让界面上那张还挂着的卡立刻收尾，否则：
          // 1) 用户对着一张早已失效的卡发呆，点下去只会得到"审批请求已过期"；
          // 2) 队列被这张卡卡住，后面的审批永远轮不到。
          //
          // 不在这里记 ledger：事件只带 requestId/decision，**不带用户写的意见**，
          // 而"用户为什么拒绝/同意时补了什么要求"正是最该留档的部分。
          // 客户端应答时拿到完整答案，所以由应答处落库（见 pumpApprovals）。
          settledApprovalIds.add(ev.requestId);
          context.onApprovalSettled?.(ev.requestId, ev.unanswered === true ? 'unanswered' : 'settled');
          break;
        }

        case 'question-request': {
          // 完整落库：AI 的提问与选项
          ledger.record('question-request', {
            questionId: ev.questionId,
            question: ev.question,
            options: ev.options,
            allowMultiple: ev.allowMultiple,
            allowFreeText: ev.allowFreeText,
          }, ledgerTurn);
          // AI 主动提问（ask_user 工具）。与审批不同：**不放行写操作**，
          // 所以不排队、不 fail-closed；用户没答就如实回一个 unanswered，让 AI 等下一步指示。
          void (async () => {
            try {
              // 提问同样不设超时，等用户回答期间看门狗不计时
              beginAwaitUser();
              let answer: { selected: string[]; text?: string };
              try {
                answer = context.onAskUser
                  ? await context.onAskUser({
                      questionId: ev.questionId,
                      question: ev.question,
                      options: ev.options ?? [],
                      ...(ev.allowMultiple ? { allowMultiple: true } : {}),
                      ...(ev.allowFreeText ? { allowFreeText: true } : {}),
                    })
                  : { selected: [], text: '（当前界面不支持提问）' };
              } finally {
                endAwaitUser();
              }
              // 服务端可能**已经**结算了这个提问（没等到回答，或用户在别处答过）。
              // 此时再 POST 只会拿到 400「提问已过期或不存在」，界面上凭空多出一条
              // "⚠ 提问处理失败" —— 明明什么都没出错。所以已结算的直接跳过。
              if (settledQuestionIds.has(ev.questionId)) {
                settledQuestionIds.delete(ev.questionId);
                return;
              }
              // 完整落库：**用户对提问的回答**（AI 后续判断的关键输入，以前不入库）
              ledger.record('question-answered', {
                questionId: ev.questionId,
                selected: answer.selected,
                text: answer.text,
              }, ledgerTurn);
              await answerQuestion(sid, ev.questionId, answer);
              // 送达后不再需要守卫（事件可能比响应先到，所以这里再删一次）
              settledQuestionIds.delete(ev.questionId);
            } catch (e) {
              callbacks.onAgentMessage?.(`⚠ 提问处理失败：${e instanceof Error ? e.message : String(e)}`);
            }
          })();
          break;
        }

        case 'question-answered': {
          settledQuestionIds.add(ev.questionId);
          context.onQuestionSettled?.(ev.questionId, ev.unanswered === true ? 'unanswered' : 'answered');
          break;
        }

        case 'assistant-message': {
          iterations++;
          // 本步骤完整文本 = 最终回答候选（工具步骤亦如此，保证"生成内容为空"不再出现）
          content = ev.text || content;

          const stepDialogue = stepText.trim();
          const hadThinking = stepThinking.trim().length > 0;

          // 完整落库：先结转本步骤的 reasoning（它逻辑上先于回答），再记回答本身。
          // 顺序很重要 —— 重建时 reasoning 应出现在它所解释的那条回答之前。
          flushReasoning();
          ledger.record('assistant-message', {
            step: iterations,
            text: ev.text ?? '',
            // 本步骤的可见对话文本（与 ev.text 在部分情况下不同，两者都留，避免丢信息）
            stepDialogue,
            hadToolCall: stepHasToolCall,
            model: model,
            provider,
          }, ledgerTurn);

          if (stepHasToolCall) {
            const stepDialogueLong = stepDialogue.length > 200;
            if (stepDialogueLong) {
              // 过长说明文字：几乎必然是最终回答（模型把总结写进了带工具调用的步骤）。
              // 不在中间静态全量显示，作为最终回答在回合结束后打字机回放。
              finalText = stepDialogue;
              tr('STEP', `promote long tool-step text to final (len=${stepDialogue.length})`);
            } else if (stepDialogue) {
              // 工具步骤：短说明文字按"对话"静态呈现一次
              tr('EMIT', `onAgentMessage len=${stepDialogue.length} text=${stepDialogue.slice(0, 60)}`);
              callbacks.onAgentMessage?.(stepDialogue);
            }
            // 思考已在 reasoning-delta 时实时流入思考标签，不再出独立静态标签
          } else {
            // 非工具步骤（最终回答）：记录待回放内容，统一在回合结束回放
            finalText = stepDialogue || ev.text || '';
            tr('STEP', `final captured text_len=${finalText.length}`);
          }

          // 思考阶段结束：闭合当前"深度思考中"标签（下一段思考将新开标签）
          if (hadThinking) {
            callbacks.onThinkingPhaseEnd?.();
            tr('EMIT', 'onThinkingPhaseEnd');
          }

          stepText = '';
          stepThinking = '';
          stepHasToolCall = false;
          break;
        }

        case 'turn-end': {
          // 思考若在步骤被截断（未到 assistant-message），兜底闭合标签
          if (stepThinking.trim().length > 0) {
            callbacks.onThinkingPhaseEnd?.();
            tr('EMIT', 'onThinkingPhaseEnd(turn-end)');
          }
          // 完整落库：回合边界（重建时据此知道"这一轮到哪里结束"），
          // 并把可能残留的 reasoning 一并结转，确保没有任何内容漏写。
          flushReasoning();
          ledger.record('turn-end', { turn: ev.turn, reason: ev.reason, finalText: finalText || undefined }, ledgerTurn);
          // 统一回放最终回答（正文打字机）：一次、有序、无交错。
          // 思考已在各步骤实时流入，无需在回合末回放；onContent 会关闭思考标签。
          if (finalText.trim()) {
            tr('REPLAY', `start final text_len=${finalText.length}`);
            pendingReplays.push(typeOut(finalText, (p) => callbacks.onContent?.(p)));
            tr('REPLAY', 'queued');
          }
          // 等回放完成再收尾，避免面板在打字中提前标记完成
          void Promise.allSettled(pendingReplays).then(() => finish());
          break;
        }

        case 'error': {
          // 完整落库：错误也是历史的一部分（否则重建时无法解释"这轮为什么没有回答"）
          flushReasoning();
          ledger.record('error', { message: ev.message }, ledgerTurn);
          fail(new Error(ev.message));
          break;
        }

        case 'user-message': {
          // 引擎回显本轮用户消息：确认它真的进了会话上下文。
          // 若客户端已先记过（正常路径），这里按文本去重 —— 不重复写库，
          // 但把"引擎已收到"作为一条独立事实补充进去，便于事后核对。
          if (!ledgerUserTexts.has(ev.text)) {
            ledgerUserMessage(ev.text);
          } else {
            ledger.record('user-message-echo', { text: ev.text }, ledgerTurn);
          }
          break;
        }

        case 'turn-start': {
          // 引擎侧的回合编号是权威：用它覆盖本地推断值，使 turn 字段跨设备一致
          if (Number.isInteger(ev.turn) && ev.turn > 0) ledgerTurn = ev.turn;
          break;
        }

        default:
          break;
      }
    }

    // 附着模式**不发消息**：服务端那个回合早就发过了，正停在审批/提问上等着。
    // 这里只负责把待办接回来（订阅时服务端会补发），用户答复后那个回合自己继续跑。
    if (!attachOnly) {
      // 完整落库：先把本轮用户消息写进流水（它是一轮历史的起点），再发给引擎。
      // 引擎收到后会回显一条 user-message，由 ledgerUserMessage 去重，避免重复。
      ledgerTurn = 1;
      ledgerUserMessage(userText);
      void sendSessionMessage(sid, userText).catch((e) => fail(new Error(`发送失败：${e instanceof Error ? e.message : String(e)}`)));
    }
  });
}

/**
 * 取某个 UI 会话（convId）对应的服务端会话 id。
 *
 * 供面板在**页面刷新/重连之后**判断"这个对话是否还有挂在服务端的回合"
 * （审批不设超时，所以服务端可能一直在等），从而决定要不要附着回去。
 */
export function getHarnessSessionIdForConv(convId: string): string | undefined {
  return sessionsByConv.get(convId);
}

/**
 * 是否有回合正在进行。
 *
 * "进行中"包括两种：模型正在生成，以及**停在审批/提问上等用户决策**
 * （审批不设超时，所以后者可能持续很久，回合其实没结束）。
 * 用途：用户想刷新/关页/离开作品时，界面据此提醒"会话还在进行，确定要走吗"——
 * 否则用户会以为离开就等于取消，而服务端其实还在跑、还在等（这在生产上是真实困惑）。
 *
 * 用模块级计数（`activeTurns`，声明在文件顶部）而不是 React state：
 * "返回书架"按钮在 Workspace 之外（Editor 外壳），拿不到面板内部状态，
 * 但读这个同步函数就够了。
 */
export function isHarnessTurnActive(): boolean {
  return activeTurns > 0;
}

/** 关闭某个 UI 会话对应的引擎会话（如会话被删除时调用） */
export async function disposeHarnessSession(convId: string): Promise<void> {
  const sid = sessionsByConv.get(convId);
  if (!sid) return;
  sessionsByConv.delete(convId);
  sessionEffortByConv.delete(convId);
  persistSessions();
  await closeSession(sid).catch((): void => undefined);
}
